import type { ComponentType } from 'react';
import type { ReportViewProps } from './reportTypes';

import SalesPerformanceReport from '@/pages/reports/views/SalesPerformanceReport';
import MenuInsightsReport from '@/pages/reports/views/MenuInsightsReport';
import OperationsOverviewReport from '@/pages/reports/views/OperationsOverviewReport';
import SalesDetailReport from '@/pages/reports/views/SalesDetailReport';
import SalesBalanceReport from '@/pages/reports/views/SalesBalanceReport';
import SalesByHourReport from '@/pages/reports/views/SalesByHourReport';
import TipsReport from '@/pages/reports/views/TipsReport';
import CreditCardReport from '@/pages/reports/views/CreditCardReport';
import CustomPaymentReport from '@/pages/reports/views/CustomPaymentReport';
import PosTransactionsReport from '@/pages/reports/views/PosTransactionsReport';
import EndOfDayReport from '@/pages/reports/views/EndOfDayReport';
import TaxReport from '@/pages/reports/views/TaxReport';
import DiscountReport from '@/pages/reports/views/DiscountReport';
import DeliveryChargeReport from '@/pages/reports/views/DeliveryChargeReport';
import ExceptionsReport from '@/pages/reports/views/ExceptionsReport';
import MenuUsageReport from '@/pages/reports/views/MenuUsageReport';
import ItemsStatisticsReport from '@/pages/reports/views/ItemsStatisticsReport';
import CategoryGroupSalesReport from '@/pages/reports/views/CategoryGroupSalesReport';
import SalesAreaReport from '@/pages/reports/views/SalesAreaReport';
import FoodVsDrinkReport from '@/pages/reports/views/FoodVsDrinkReport';
import AlcoholFoodDrinkReport from '@/pages/reports/views/AlcoholFoodDrinkReport';
import ServerSalesReport from '@/pages/reports/views/ServerSalesReport';
import ServerRankingReport from '@/pages/reports/views/ServerRankingReport';
import ShopFloorReport from '@/pages/reports/views/ShopFloorReport';

export type ReportTab = 'sales' | 'menu' | 'operations';

export interface ReportDef {
  /** Stable id used in the ?report= query param. */
  id: string;
  /** Label shown in the combobox. */
  label: string;
  /** Which page/tab this report belongs to. */
  tab: ReportTab;
  /** The view component to render. ComingSoonReport until implemented. */
  Component: ComponentType<ReportViewProps>;
  /** false → rendered as a "coming soon" placeholder and flagged in the selector. */
  ready: boolean;
}

/**
 * Catalogue of every report surfaced in the dashboard, grouped by tab.
 * The first entry for each tab is the default selection.
 *
 * As each report is implemented, swap `Component` to its real view and set
 * `ready: true`. The selector and page shells need no other changes.
 */
export const REPORTS: ReportDef[] = [
  // ---- Sales tab ----
  { id: 'sales-performance', label: 'Sales Overview', tab: 'sales', Component: SalesPerformanceReport, ready: true },
  { id: 'sales-detail', label: 'Sales Detail', tab: 'sales', Component: SalesDetailReport, ready: true },
  { id: 'sales-balance', label: 'Sales Balance', tab: 'sales', Component: SalesBalanceReport, ready: true },
  { id: 'sales-by-hour', label: 'Sales by Hour', tab: 'sales', Component: SalesByHourReport, ready: true },
  { id: 'tips', label: 'Tips', tab: 'sales', Component: TipsReport, ready: true },
  { id: 'credit-card', label: 'Credit Card', tab: 'sales', Component: CreditCardReport, ready: true },
  { id: 'custom-payment', label: 'Custom Payment', tab: 'sales', Component: CustomPaymentReport, ready: true },
  { id: 'pos-transactions', label: 'POS Transactions', tab: 'sales', Component: PosTransactionsReport, ready: true },
  { id: 'end-of-day', label: 'End-of-Day Summary', tab: 'sales', Component: EndOfDayReport, ready: true },
  { id: 'tax', label: 'Tax', tab: 'sales', Component: TaxReport, ready: true },
  { id: 'discount', label: 'Discount', tab: 'sales', Component: DiscountReport, ready: true },
  { id: 'delivery-charge', label: 'Delivery Charge', tab: 'sales', Component: DeliveryChargeReport, ready: true },
  { id: 'exceptions', label: 'Exceptions', tab: 'sales', Component: ExceptionsReport, ready: true },

  // ---- Menu tab ----
  { id: 'menu-insights', label: 'Menu Overview', tab: 'menu', Component: MenuInsightsReport, ready: true },
  { id: 'menu-usage', label: 'Menu Usage', tab: 'menu', Component: MenuUsageReport, ready: true },
  { id: 'items-statistics', label: 'Items Statistics', tab: 'menu', Component: ItemsStatisticsReport, ready: true },
  { id: 'category-group-sales', label: 'Category / Group Sales', tab: 'menu', Component: CategoryGroupSalesReport, ready: true },
  { id: 'sales-area', label: 'Sales Area', tab: 'menu', Component: SalesAreaReport, ready: true },
  { id: 'food-drink', label: 'Food vs Drink', tab: 'menu', Component: FoodVsDrinkReport, ready: true },
  { id: 'alcohol-food-drink', label: 'Alcohol / Food / Drink', tab: 'menu', Component: AlcoholFoodDrinkReport, ready: true },

  // ---- Operations tab ----
  { id: 'operations', label: 'Operations Overview', tab: 'operations', Component: OperationsOverviewReport, ready: true },
  { id: 'server-sales', label: 'Server Sales', tab: 'operations', Component: ServerSalesReport, ready: true },
  { id: 'server-ranking', label: 'Server Ranking', tab: 'operations', Component: ServerRankingReport, ready: true },
  { id: 'shop-floor', label: 'Shop Floor / Table Sales', tab: 'operations', Component: ShopFloorReport, ready: true },
];

export function reportsForTab(tab: ReportTab): ReportDef[] {
  return REPORTS.filter((r) => r.tab === tab);
}

export function defaultReportId(tab: ReportTab): string {
  return reportsForTab(tab)[0].id;
}

/** Resolve a report by id, constrained to a tab (falls back to the tab default). */
export function resolveReport(tab: ReportTab, id: string | null): ReportDef {
  const inTab = reportsForTab(tab);
  return inTab.find((r) => r.id === id) ?? inTab[0];
}
